import React, { useState } from 'react';
import { Calendar, Mail, Phone, Send, Plus, Trash2, X, Check } from 'lucide-react';
import { format, addWeeks } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useThemeStore } from '../stores/themeStore';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../config/firebase';
import { toast } from 'react-hot-toast';

interface FollowUpStep {
  id: number;
  date: Date;
  type: 'call' | 'email' | 'newsletter' | 'phone_follow_up' | 'email_follow_up';
  status: 'pending' | 'completed' | 'skipped';
  description: string;
}

interface FollowUpSchedulerProps {
  initialContactDate: Date;
  onScheduleAction: (step: FollowUpStep) => void;
}

function FollowUpScheduler({ initialContactDate, onScheduleAction }: FollowUpSchedulerProps) {
  const { darkMode } = useThemeStore();
  const [customizing, setCustomizing] = useState(false);
  const [followUpSteps, setFollowUpSteps] = useState<FollowUpStep[]>([
    {
      id: 1,
      date: addWeeks(initialContactDate, 1),
      type: 'call',
      status: 'pending',
      description: 'Premier suivi - Présentation des services Anthea'
    },
    {
      id: 2,
      date: addWeeks(initialContactDate, 2),
      type: 'email',
      status: 'pending',
      description: 'Newsletter - Profils disponibles'
    },
    {
      id: 3,
      date: addWeeks(initialContactDate, 3),
      type: 'newsletter',
      status: 'pending',
      description: 'Présentation des candidatures multi-agences'
    },
    {
      id: 4,
      date: addWeeks(initialContactDate, 4),
      type: 'phone_follow_up',
      status: 'pending',
      description: 'Relance téléphonique'
    },
    {
      id: 5,
      date: addWeeks(initialContactDate, 5),
      type: 'email_follow_up',
      status: 'pending',
      description: 'Relance par email'
    }
  ]);
  
  const [newStep, setNewStep] = useState<Omit<FollowUpStep, 'id' | 'status'>>({
    date: addWeeks(initialContactDate, 1),
    type: 'call',
    description: ''
  });

  const getActionIcon = (type: string) => {
    switch (type) {
      case 'call':
        return <Phone className="w-5 h-5" />;
      case 'email':
        return <Mail className="w-5 h-5" />;
      case 'newsletter':
        return <Send className="w-5 h-5" />;
      case 'phone_follow_up':
        return <Phone className="w-5 h-5" />;
      case 'email_follow_up':
        return <Mail className="w-5 h-5" />;
      default:
        return null;
    }
  };
  
  const handleAddStep = () => {
    if (!newStep.description.trim()) return;
    
    const newId = Math.max(0, ...followUpSteps.map(s => s.id)) + 1;
    const stepToAdd: FollowUpStep = {
      ...newStep,
      id: newId,
      status: 'pending'
    };
    
    setFollowUpSteps([...followUpSteps, stepToAdd]);
    setNewStep({
      date: addWeeks(initialContactDate, 1),
      type: 'call',
      description: ''
    });
  };
  
  const handleRemoveStep = (id: number) => {
    setFollowUpSteps(followUpSteps.filter(step => step.id !== id));
  };
  
  const handleSaveCustomization = () => {
    setCustomizing(false);
  };

  const handleScheduleAction = async (step: FollowUpStep) => {
    if (!auth.currentUser) {
      toast.error('Vous devez être connecté pour planifier une action');
      return;
    }

    try {
      // Créer un rendez-vous dans la collection appointments
      const appointmentData = {
        title: `Suivi: ${step.description}`,
        date: step.date,
        time: '09:00', // Heure par défaut
        agency: 'Marseille 4', // Agence par défaut
        contact: 'Contact entreprise',
        priority: 'medium' as const,
        userId: auth.currentUser.uid,
        createdAt: serverTimestamp(),
        status: 'pending'
      };

      const docRef = await addDoc(collection(db, 'appointments'), appointmentData);
      
      // Créer une alerte pour ce rendez-vous
      const alertData = {
        type: 'rendez-vous',
        company: appointmentData.title,
        date: step.date,
        agency: appointmentData.agency,
        status: 'pending',
        step: step.id,
        description: step.description,
        action: `Action de suivi: ${
          step.type === 'call' ? 'Appel' : 
          step.type === 'email' ? 'Email' : 
          step.type === 'newsletter' ? 'Newsletter' :
          step.type === 'phone_follow_up' ? 'Relance téléphonique' :
          'Relance par email'
        }`,
        userId: auth.currentUser.uid,
        appointmentId: docRef.id,
        emailSent: false,
        displayed: false,
        createdAt: serverTimestamp()
      };

      await addDoc(collection(db, 'alerts'), alertData);
      
      // Appeler la fonction de callback
      onScheduleAction(step);
      
      toast.success('Action planifiée avec succès');
    } catch (error) {
      console.error('Error scheduling action:', error);
      toast.error('Erreur lors de la planification de l\'action');
    }
  };

  return (
    <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm`}>
      <div className="p-6">
        <h2 className={`text-lg font-semibold mb-6 flex items-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          <Calendar className="w-5 h-5 mr-2 text-blue-600" />
          Planning de suivi
        </h2>

        <div className="space-y-6">
          {customizing ? (
            <div className="space-y-6">
              {/* Liste des étapes existantes avec possibilité de suppression */}
              {followUpSteps.map((step) => (
                <div
                  key={step.id}
                  className={`flex items-start space-x-4 p-4 rounded-lg border ${
                    darkMode 
                      ? 'border-gray-700 bg-gray-700/30' 
                      : 'border-gray-100 bg-gray-50'
                  } transition-colors`}
                >
                  <div className={`p-2 rounded-lg ${
                    step.type === 'call' || step.type === 'phone_follow_up'
                      ? darkMode ? 'bg-green-900 text-green-400' : 'bg-green-100 text-green-600' 
                      : step.type === 'email' || step.type === 'email_follow_up'
                      ? darkMode ? 'bg-blue-900 text-blue-400' : 'bg-blue-100 text-blue-600' 
                      : darkMode ? 'bg-purple-900 text-purple-400' : 'bg-purple-100 text-purple-600'
                  }`}>
                    {getActionIcon(step.type)}
                  </div>

                  <div className="flex-1">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{step.description}</h3>
                        <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          Prévu le {format(step.date, 'd MMMM yyyy', { locale: fr })}
                        </p>
                      </div>
                      <button
                        onClick={() => handleRemoveStep(step.id)}
                        className={`p-1 ${darkMode ? 'text-red-400 hover:bg-red-900/30' : 'text-red-600 hover:bg-red-50'} rounded-lg`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              
              {/* Formulaire d'ajout d'une nouvelle étape */}
              <div className={`p-4 border ${darkMode ? 'border-gray-700' : 'border-gray-200'} rounded-lg`}>
                <h3 className={`font-medium mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Ajouter une étape</h3>
                
                <div className="space-y-4">
                  <div>
                    <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-1`}>
                      Type d'action
                    </label>
                    <select
                      value={newStep.type}
                      onChange={(e) => setNewStep({...newStep, type: e.target.value as any})}
                      className={`w-full rounded-lg border ${darkMode ? 'border-gray-700 bg-gray-700 text-white' : 'border-gray-200'} p-2.5`}
                    >
                      <option value="call">Appel téléphonique</option>
                      <option value="email">Email</option>
                      <option value="newsletter">Newsletter</option>
                      <option value="phone_follow_up">Relance téléphonique</option>
                      <option value="email_follow_up">Relance par email</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-1`}>
                      Date
                    </label>
                    <input
                      type="date"
                      value={format(newStep.date, 'yyyy-MM-dd')}
                      onChange={(e) => setNewStep({...newStep, date: new Date(e.target.value)})}
                      className={`w-full rounded-lg border ${darkMode ? 'border-gray-700 bg-gray-700 text-white' : 'border-gray-200'} p-2.5`}
                    />
                  </div>
                  
                  <div>
                    <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-1`}>
                      Description
                    </label>
                    <input
                      type="text"
                      value={newStep.description}
                      onChange={(e) => setNewStep({...newStep, description: e.target.value})}
                      placeholder="Description de l'action"
                      className={`w-full rounded-lg border ${darkMode ? 'border-gray-700 bg-gray-700 text-white' : 'border-gray-200'} p-2.5`}
                    />
                  </div>
                  
                  <button
                    onClick={handleAddStep}
                    disabled={!newStep.description.trim()}
                    className={`flex items-center px-4 py-2 ${darkMode ? 'bg-blue-700 text-white' : 'bg-blue-600 text-white'} rounded-lg hover:${darkMode ? 'bg-blue-600' : 'bg-blue-700'} disabled:opacity-50`}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Ajouter cette étape
                  </button>
                </div>
              </div>
              
              {/* Boutons d'action */}
              <div className="flex justify-end gap-4 mt-6">
                <button
                  onClick={() => setCustomizing(false)}
                  className={`px-4 py-2 ${darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'} rounded-lg`}
                >
                  Annuler
                </button>
                <button
                  onClick={handleSaveCustomization}
                  className={`flex items-center px-4 py-2 ${darkMode ? 'bg-green-700 text-white' : 'bg-green-600 text-white'} rounded-lg hover:${darkMode ? 'bg-green-600' : 'bg-green-700'}`}
                >
                  <Check className="w-4 h-4 mr-2" />
                  Enregistrer le planning
                </button>
              </div>
            </div>
          ) : (
            <>
              {followUpSteps.map((step) => (
                <div
                  key={step.id}
                  className={`flex items-start space-x-4 p-4 rounded-lg border ${
                    darkMode 
                      ? 'border-gray-700 hover:border-blue-800 hover:bg-blue-900/20' 
                      : 'border-gray-100 hover:border-blue-100 hover:bg-blue-50'
                  } transition-colors`}
                >
                  <div className={`p-2 rounded-lg ${
                    step.type === 'call' || step.type === 'phone_follow_up'
                      ? darkMode ? 'bg-green-900 text-green-400' : 'bg-green-100 text-green-600' 
                      : step.type === 'email' || step.type === 'email_follow_up'
                      ? darkMode ? 'bg-blue-900 text-blue-400' : 'bg-blue-100 text-blue-600' 
                      : darkMode ? 'bg-purple-900 text-purple-400' : 'bg-purple-100 text-purple-600'
                  }`}>
                    {getActionIcon(step.type)}
                  </div>

                  <div className="flex-1">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{step.description}</h3>
                        <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          Prévu le {format(step.date, 'd MMMM yyyy', { locale: fr })}
                        </p>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleScheduleAction(step)}
                          className="px-3 py-1 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                        >
                          Planifier
                        </button>
                      </div>
                    </div>

                    {step.type === 'newsletter' && (
                      <div className={`mt-3 p-3 ${darkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-lg text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        <p>Contenu de la newsletter :</p>
                        <ul className="list-disc ml-4 mt-1">
                          <li>Présentation du cabinet Anthea</li>
                          <li>Profils disponibles dans votre secteur</li>
                          <li>Succès stories et témoignages</li>
                        </ul>
                      </div>
                    )}
                    
                    {step.type === 'phone_follow_up' && (
                      <div className={`mt-3 p-3 ${darkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-lg text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        <p>Points à aborder lors de la relance téléphonique :</p>
                        <ul className="list-disc ml-4 mt-1">
                          <li>Suivi des profils envoyés précédemment</li>
                          <li>Vérification des besoins actuels en recrutement</li>
                          <li>Proposition de nouveaux profils disponibles</li>
                        </ul>
                      </div>
                    )}
                    
                    {step.type === 'email_follow_up' && (
                      <div className={`mt-3 p-3 ${darkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-lg text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        <p>Contenu de l'email de relance :</p>
                        <ul className="list-disc ml-4 mt-1">
                          <li>Rappel des échanges précédents</li>
                          <li>Mise à jour des profils disponibles</li>
                          <li>Proposition de rendez-vous</li>
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              ))}

              <div className={`mt-6 pt-6 border-t ${darkMode ? 'border-gray-700' : 'border-gray-100'}`}>
                <div className="flex items-center justify-between">
                  <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Premier contact : {format(initialContactDate, 'd MMMM yyyy', { locale: fr })}
                  </span>
                  <button 
                    onClick={() => setCustomizing(true)}
                    className={`text-sm ${darkMode ? 'text-blue-400' : 'text-blue-600'} hover:${darkMode ? 'text-blue-300' : 'text-blue-700'}`}
                  >
                    Personnaliser le planning
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default FollowUpScheduler;